import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { View, Text, FlatList, Dimensions } from 'react-native'
import _ from 'lodash'
import spacing from '../../styles/spacing'
import colors from '../../styles/colors'
import fonts from '../../styles/fonts'
import { TouchableOpacity, StyleSheet } from 'react-native'
import { formatDataRows, getKey, getLabel } from './SelectWithData'
import { onValueToggleWrapped } from './SelectMultipleWithData'
import { MSFESCheckbox } from '../MSFESCheckbox'

export const CheckboxMultiselectWithData = React.forwardRef((props, ref) => {
  const {
    onChangeText,
    style,
    dataRows = [],
    editable = true,
    valueField = 'id',
    keyField = 'id',
    labelField = 'name',
    keyFieldResolver = false
  } = props

  const screenHeight = Dimensions.get('window').height

  const [localSelectedItems, setLocalSelectedItems] = useState(new Set())

  useEffect(() => {
    if (props.value) {
      const valueArray = _.castArray(props.value)
      const selectedKeys = valueArray.map((v) =>
        getKey(v, keyFieldResolver, keyField)
      )
      setLocalSelectedItems(new Set(selectedKeys))
    } else {
      setLocalSelectedItems(new Set())
    }
  }, [props.value, keyFieldResolver, keyField])

  useEffect(() => {
    if (props.defaultValue && props.data?.entity?.mode === 'add') {
      // this is a new record with a default value (e.g. calculated).
      // we probably need to propagate it up.
      onChangeText(props.defaultValue)
    }
  }, [props.defaultValue])

  const dataFormatted = useMemo(() => {
    return formatDataRows(
      dataRows,
      keyFieldResolver,
      keyField,
      valueField,
      labelField
    )
  }, [dataRows, keyFieldResolver, keyField, valueField, labelField])

  let inputColor

  if (props.dark) {
    inputColor = colors.inputTextDark
  } else {
    inputColor = colors.inputText
  }

  const inputStyle = [
    props.inputStyle,
    {
      color: inputColor,
      fontFamily: fonts.inputTextFace
    }
  ]

  const handleClick = useCallback(
    (item) => {
      const key = getKey(item, keyFieldResolver, keyField)

      setLocalSelectedItems((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(key)) {
          newSet.delete(key)
        } else {
          newSet.add(key)
        }
        return newSet
      })

      onValueToggleWrapped(
        item,
        dataRows,
        props.defaultValue,
        keyField,
        keyFieldResolver,
        onChangeText
      )
    },
    [keyFieldResolver, keyField, dataRows, props.defaultValue, onChangeText]
  )

  const isItemSelected = useCallback((key) => localSelectedItems.has(key), [
    localSelectedItems
  ])

  const renderItem = useCallback(
    ({ item, index }) => {
      const key = getKey(item, keyFieldResolver, keyField)
      const active = isItemSelected(key)
      const isOdd = index % 2
      const label = getLabel(item, labelField)

      return (
        <TouchableOpacity
          disabled={!editable}
          onPress={() => handleClick(item)}
          style={[styles.itemContainer, isOdd && styles.oddItem]}
        >
          <Text
            style={[styles.itemText, inputStyle, active && styles.activeText]}
          >
            {label}
          </Text>
          <MSFESCheckbox
            radio={false}
            disabled={!editable}
            onPress={() => handleClick(item)}
            checked={active}
            style={styles.checkbox}
          />
        </TouchableOpacity>
      )
    },
    [
      keyFieldResolver,
      keyField,
      labelField,
      isItemSelected,
      handleClick,
      editable,
      inputStyle
    ]
  )

  const keyExtractor = useCallback(
    (item) => getKey(item, keyFieldResolver, keyField),
    [keyFieldResolver, keyField]
  )

  return (
    <View style={style}>
      <View style={[styles.listContainer, { maxHeight: screenHeight * 0.7 }]}>
        <FlatList
          data={dataFormatted}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          extraData={localSelectedItems}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          windowSize={10}
          initialNumToRender={10}
          getItemLayout={undefined}
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={false}
        />
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  listContainer: {},
  itemContainer: {
    flexDirection: 'row',
    paddingTop: spacing.m0_5,
    marginLeft: -spacing.m2
  },
  oddItem: {
    backgroundColor: 'white'
  },
  itemText: {
    flex: 1,
    opacity: 0.7,
    marginLeft: spacing.m2,
    marginTop: spacing.m1
  },
  activeText: {
    opacity: 1
  },
  checkbox: {
    marginRight: 4
  }
})

export default CheckboxMultiselectWithData
